import React, { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Alert, Box, LinearProgress, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { useAdminData } from './shared'

const timeZones = ['America/Los_Angeles', 'UTC']
const integer = value => value == null ? '—' : new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value))
const money = value => value == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value))
const dateInTimeZone = (date, timeZone) => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date).filter(part => part.type !== 'literal').map(part => [part.type, part.value]))
  return `${parts.year}-${parts.month}-${parts.day}`
}
const shiftDate = (date, days) => {
  const shifted = new Date(`${date}T00:00:00Z`)
  shifted.setUTCDate(shifted.getUTCDate() + days)
  return shifted.toISOString().slice(0, 10)
}
const defaultPeriod = () => {
  const to = dateInTimeZone(new Date(), timeZones[0])
  return [shiftDate(to, -29), to]
}
const normalizeParams = params => {
  const next = new URLSearchParams()
  const [defaultFrom, defaultTo] = defaultPeriod()
  next.set('from', params.get('from') || defaultFrom)
  next.set('to', params.get('to') || defaultTo)
  next.set('timeZone', timeZones.includes(params.get('timeZone')) ? params.get('timeZone') : timeZones[0])
  return next
}

const entityMetrics = [
  ['parentAccounts', 'Accounts', 'Parent Business Accounts.'],
  ['businessAccounts', 'Businesses', 'Individual Businesses within parent Accounts.'],
  ['activeOffers', 'Active offers', 'Offers that are active at the current point in time.'],
  ['businessUsers', 'Business users', 'Users who currently have the Business role.'],
  ['standardUsers', 'Standard users', 'Users whose only account role is Standard user.'],
]
const periodEntityMetrics = entityMetrics.map(([key, label, definition]) => [
  key,
  key === 'activeOffers' ? 'Active offers in period' : `New ${label.toLowerCase()}`,
  key === 'activeOffers' ? 'Active or now-expired Offers whose validity overlaps the selected period.' : `${definition} Created in the selected period.`,
])
const activityMetrics = [
  ['claims', 'Claims', 'Authoritative successful Offer Claims.', integer],
  ['clicks', 'Clicks', 'Recorded Offer click events. Claim clicks are included.', integer],
  ['impressions', 'Impressions', 'Recorded Offer impressions.', integer],
  ['revenue', 'Revenue', 'Paid and promotional value spent on Offer actions.', money],
  ['cardCredits', 'Card credits', 'Credits funded through successful card charges.', money],
  ['couponCredits', 'Coupon credits', 'Promotional credits issued by HiddenUnderGround.', money],
]

function MetricGrid({ metrics, items }) {
  return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: `repeat(${Math.min(items.length, 6)}, minmax(0, 1fr))` }, borderTop: '1px solid', borderLeft: '1px solid', borderColor: 'divider' }}>
    {items.map(([key, label, definition, render = integer]) => <Box key={key} sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0, borderRight: '1px solid', borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}>
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Tooltip title={definition}><InfoOutlined sx={{ fontSize: 15, color: 'text.disabled' }} /></Tooltip>
      </Stack>
      <Typography variant="h5" fontWeight={700} mt={0.75}>{render(metrics?.[key])}</Typography>
    </Box>)}
  </Box>
}

function OverviewPanel({ title, totalLabel, totalMetrics, periodMetrics, items, periodItems = items }) {
  return <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
    <Box px={{ xs: 2, md: 2.5 }} py={2} bgcolor="#173F5F" color="#fff">
      <Typography variant="h6" fontWeight={650}>{title}</Typography>
    </Box>
    <Box px={{ xs: 2, md: 2.5 }} pt={2}><Typography variant="body2" fontWeight={650} color="text.secondary">{totalLabel}</Typography></Box>
    <Box px={{ xs: 2, md: 2.5 }} pb={2.5}><MetricGrid metrics={totalMetrics} items={items} /></Box>
    <Box px={{ xs: 2, md: 2.5 }} pt={2} borderTop="1px solid" borderColor="divider" bgcolor="#F3F7FA">
      <Typography variant="body2" fontWeight={650} color="text.secondary">Selected period</Typography>
    </Box>
    <Box px={{ xs: 2, md: 2.5 }} pb={2.5} bgcolor="#F3F7FA"><MetricGrid metrics={periodMetrics} items={periodItems} /></Box>
  </Paper>
}

export default function Overview() {
  const [searchParams, setSearchParams] = useSearchParams()
  const normalized = useMemo(() => normalizeParams(searchParams), [searchParams])
  const query = normalized.toString()
  useEffect(() => {
    if (searchParams.toString() !== query) setSearchParams(normalized, { replace: true })
  }, [normalized, query, searchParams, setSearchParams])
  const { data, error, isLoading } = useAdminData(`/admin/overview?${query}`)
  const period = data?.selectedPeriod || {}
  const update = (key, value) => {
    const next = new URLSearchParams(normalized)
    next.set(key, value)
    setSearchParams(next)
  }

  return <Stack spacing={2.5}>
    <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ lg: 'center' }} gap={2}>
      <Box>
        <Typography color="text.secondary">A direct view of site-wide accounts, activity, and financial totals.</Typography>
        <Typography variant="caption" color="text.secondary">These metrics come from operational records and do not depend on Reports enablement or preparation.</Typography>
      </Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={normalized.get('from')} onChange={event => update('from', event.target.value)} />
        <TextField size="small" type="date" label="Through" InputLabelProps={{ shrink: true }} value={normalized.get('to')} onChange={event => update('to', event.target.value)} />
        <TextField select size="small" label="Time zone" value={normalized.get('timeZone')} onChange={event => update('timeZone', event.target.value)} sx={{ minWidth: 210 }}>
          {timeZones.map(zone => <MenuItem key={zone} value={zone}>{zone}</MenuItem>)}
        </TextField>
      </Stack>
    </Stack>
    {isLoading && <LinearProgress aria-label="Loading Overview" />}
    {error && <Alert severity="error">Overview metrics could not be loaded. Check the selected period and try again.</Alert>}
    {!error && <>
      <OverviewPanel title="Accounts and users" totalLabel="Current site totals" totalMetrics={data} periodMetrics={period} items={entityMetrics} periodItems={periodEntityMetrics} />
      <OverviewPanel title="Activity and finances" totalLabel="All recorded activity" totalMetrics={data} periodMetrics={period} items={activityMetrics} />
    </>}
  </Stack>
}
